import { Abas } from '@core/components/Abas';
import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { mensagemMinimo3Caracteres } from '@core/util/mensagemUtil';
import { Grid } from '@material-ui/core';
import { ContatoForm } from 'app/main/pessoa/Contato';
import { EnderecoForm } from 'app/main/pessoa/Endereco';
import { validateCnpj, validateCpf } from 'app/utils/validation';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FaTruck } from 'react-icons/fa';
import * as yup from 'yup';
import { TabContaBancaria, TabDadosGerais } from '.';
import { FornecedorActions } from '../../store/fornecedorAction';
import { useFornecedor } from '../../store/fornecedorContext';

const schemaPessoaFisica = yup
  .object()
  .shape({
    cpf: yup.string().test('test cpf', 'CPF inválido', (value) => {
      if (value) {
        const validate = validateCpf(value);
        return validate;
      }
      return true;
    }),
    identidade: yup.string().min(3, mensagemMinimo3Caracteres),
    identificadorEstrangeiro: yup.string().min(3, mensagemMinimo3Caracteres),
  })
  .nullable();

const schemaPessoaJuridica = yup
  .object()
  .shape({
    cnpj: yup.string().test('test cnpj', 'CNPJ inválido', (value) => {
      if (value) {
        const validate = validateCnpj(value);
        return validate;
      }
      return true;
    }),
    razaoSocial: yup.string().min(3, mensagemMinimo3Caracteres),
    inscricaoEstadual: yup.string().min(3, mensagemMinimo3Caracteres),
  })
  .nullable();

const schemaContatos = yup.array().of(
  yup.object().shape({
    nomeReferencia: yup.string().min(3, mensagemMinimo3Caracteres),
    email: yup.string().email('E-mail inválido'),
    emailsAdicionais: yup.array().of(
      yup.object().shape({
        email: yup.string().email('E-mail inválido'),
      })
    ),
  })
);

const schemaEndereco = yup
  .object()
  .shape({
    bairro: yup.string().min(3, mensagemMinimo3Caracteres),
    referencia: yup.string().min(3, mensagemMinimo3Caracteres),
  })
  .nullable();

const schemaConta = yup
  .object()
  .shape({
    descricao: yup.string().min(3, mensagemMinimo3Caracteres),
    contaBancaria: yup.object().shape({
      agencia: yup.number().nullable(),
      agenciaDigito: yup.number().nullable(),
      numeroConta: yup.number().nullable(),
      contaDigito: yup.number().nullable(),
      banco: yup.object().nullable(),
    }),
    tipoConta: yup
      .string()
      .nullable()
      .test('tipo.required', 'Informe o tipo da conta', function(value) {
        const {
          contaBancaria: { agencia, agenciaDigito, numeroConta, contaDigito, banco, tipoConta },
          descricao,
        } = this.parent;

        if (descricao || agencia || agenciaDigito || numeroConta || contaDigito || banco?.id) {
          return !!value || !!tipoConta;
        }

        return true;
      }),
  })
  .nullable();

const schema = yup.object().shape({
  pessoa: yup
    .object()
    .default({ nome: '', tipoPessoa: 'JURIDICA' })
    .shape({
      nome: yup
        .string()
        .required('Informe o nome')
        .min(3, mensagemMinimo3Caracteres),
      tipoPessoa: yup.string().required(),
      inscricaoMunicipal: yup.string().min(3, mensagemMinimo3Caracteres),
      pessoaFisica: schemaPessoaFisica,
      pessoaJuridica: schemaPessoaJuridica,
      contatos: schemaContatos,
      endereco: schemaEndereco,
      conta: schemaConta,
    })
    .required(),
});

const FornecedorCadastro = () => {
  const [state, dispatch] = useFornecedor();
  const { registro, cadastrar, requestParams } = state;

  const [abaSelecionada, selecionarAba] = useState(0);

  const formik = useFormik({
    validationSchema: schema,
    initialValues: registro,
    onSubmit: (fornecedor) => FornecedorActions.onSubmit(dispatch, fornecedor, requestParams),
    validateOnChange: true,
  });

  const { resetForm } = formik;
  useEffect(() => {
    if (registro) {
      if (registro?.pessoa?.pessoaJuridica?.cnpj) {
        registro.pessoa.cpfCnpj = registro?.pessoa?.pessoaJuridica?.cnpj;
      } else if (registro?.pessoa?.pessoaFisica?.cpf) {
        registro.pessoa.cpfCnpj = registro?.pessoa?.pessoaFisica?.cpf;
      }

      resetForm({
        values: registro,
      });
      selecionarAba(0);
    }
  }, [registro, resetForm]);

  const handleCancelar = () => {
    const clearFormFornecedor = (initialValues) => {
      formik.resetForm({ values: initialValues });
    };
    FornecedorActions.fecharModalCadastro(dispatch, formik.dirty, clearFormFornecedor);
  };

  const handleFetchPapel = (fornecedorId) => FornecedorActions.requestEditar(dispatch, fornecedorId);

  return (
    <SimpleDialog fullScreen open={cadastrar} onClose={handleCancelar} innerScroll>
      <FormikProvider value={formik}>
        {cadastrar && (
          <FormDialogContent
            onSubmit={formik.handleSubmit}
            fullScreen
            header={{
              title: 'Cadastro de fornecedor',
              icon: <FaTruck />,
              toolbar: (
                <>
                  <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                    Cancelar
                  </CancelButton>
                  <SaveButton color="buttonSave" type="submit">
                    Salvar
                  </SaveButton>
                </>
              ),
            }}
            contentToolbar={
              <Abas
                value={abaSelecionada}
                onChange={selecionarAba}
                tabs={[
                  {
                    label: 'Dados gerais',
                    hasError: () => {
                      return formik.errors?.pessoa?.nome && formik.touched?.pessoa?.nome;
                    },
                  },
                  { label: 'Endereço' },
                  {
                    label: 'Contato',
                    hasError: () => {
                      return formik.errors?.pessoa?.contatos?.some((contato) => {
                        return contato.email || contato?.emailsAdicionais?.some((adicional) => adicional.email);
                      });
                    },
                  },
                  { label: 'Conta bancária' },
                ]}
              />
            }
            content={
              <div className="h-full max-h-full w-full">
                <Grid container spacing={3}>
                  {abaSelecionada === 0 && <TabDadosGerais papel="fornecedor" fetchPapel={handleFetchPapel} />}
                  {abaSelecionada === 1 && <EnderecoForm />}
                  {abaSelecionada === 2 && <ContatoForm />}
                  {abaSelecionada === 3 && <TabContaBancaria />}
                </Grid>
              </div>
            }
          />
        )}
      </FormikProvider>
    </SimpleDialog>
  );
};

export default FornecedorCadastro;
